import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, Calendar, ShieldCheck, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import GatedLeadModal from "@/components/GatedLeadModal";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  "New Launch": "bg-gold/10 text-gold-dark",
  "Under Construction": "bg-blue-50 text-blue-700",
  "Ready to Move": "bg-emerald-50 text-emerald-700",
};

export default function PropertyCard({ p }) {
  const { waLink } = useSettings();
  const [gate, setGate] = useState(false);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-card transition-all duration-300 hover:-translate-y-2 hover:shadow-card-hover group flex flex-col" data-testid={`property-card-${p.slug}`}>
      <Link to={`/property/${p.slug}`} className="relative block overflow-hidden aspect-[4/3]">
        <img src={p.images?.[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute top-3 left-3 flex gap-2">
          {p.new_launch && <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gold text-navy">New Launch</span>}
          {p.trending && <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-navy text-white">Trending</span>}
        </div>
        <span className={`absolute bottom-3 right-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_COLORS[p.status] || "bg-gray-100 text-gray-700"}`}>{p.status}</span>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 text-xs text-gold-dark font-semibold mb-1">
          <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {p.builder}
        </div>
        <Link to={`/property/${p.slug}`}><h3 className="font-display text-xl text-navy leading-tight hover:text-gold-dark transition-colors">{p.name}</h3></Link>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1.5">
          <MapPin className="w-4 h-4 text-gold shrink-0" strokeWidth={1.5} /> {p.location}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {(p.configurations || []).slice(0, 4).map((c) => (
            <span key={c} className="text-xs bg-muted text-navy px-2 py-1 rounded-md font-medium">{c}</span>
          ))}
        </div>

        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-gold" /> {p.possession}</span>
          {p.rera_approved && <span className="flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> RERA</span>}
        </div>

        {p.highlights?.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{p.highlights.join(" • ")}</p>
        )}

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-muted-foreground">Starting from</p>
          <p className="font-display text-2xl text-navy font-semibold">{p.starting_price_label}</p>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button asChild variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white col-span-2" data-testid={`view-details-${p.slug}`}>
            <Link to={`/property/${p.slug}`}>View Details</Link>
          </Button>
          <Button onClick={() => setGate(true)} data-testid={`get-price-${p.slug}`} className="bg-gold text-navy hover:bg-gold-dark font-semibold">Get Price</Button>
          <a href={waLink(`Hi, I'm interested in ${p.name} by ${p.builder} at ${p.location}. Please share details.`)} target="_blank" rel="noreferrer" data-testid={`whatsapp-${p.slug}`}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-whatsapp text-white text-sm font-semibold hover:opacity-90 transition-opacity">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      </div>

      <GatedLeadModal open={gate} onOpenChange={setGate} projectName={p.name} leadType="price"
        title={`Get Pricing for ${p.name}`} description="Share your details and our advisor will send you the latest price details." />
    </div>
  );
}
